/*
 * player context
 */
import { createContext, useState, useRef } from 'react';
import { useCookies } from 'react-cookie';
import { postLessonEvent } from '@mirinae/lessons/modules/libs/lessonLib';
import GTM from '@mirinae/apis/GoogleAnalytics';
import { lessonsPaths, apiPaths, navigateExternal } from '@mirinae/shared/defines/paths';
import httpAPI from '@mirinae/lessons/modules/apis/http';
import { wireNodeTree } from '../../editor/modules/libs/lessonLib';
import { getProOrStaff } from '@mirinae/defines/datas';
import { featureFor, useProAccessStore } from '@mirinae/stores/proAccessStore';

const PlayerContext = createContext({});

const playerStateInit = {
    mainSections: [],
    featuresLesson: false,

    componentMap: null,

    progress: {},

    sectionIndex: 0,
    pageIndex: 0,
    step: 0,

    activeModes: ['step'],

    currentLesson: null,
    currentSection: null,
    currentPage: null,
    currentStep: null,

    showHint: false,
    
    glossaryPhrases: new Set(),

    displayLoginPopup: false,
    displayLoginSuccess: false,
    displaySignupPopup: false,

    accessKey: null,
};

export const usePlayerContext = (userContext, global, componentMap) => {
    const [playerState, setPlayerState] = useState({ ...playerStateInit, componentMap });
    // const [cookies, setCookies] = useCookies(['passfirst', 'survey-1.0']);
    const accessKeys = useProAccessStore(state => state.accessKeys);

    let progressContext = null;
    let navigate = null;
    const completedExercises = useRef([]);
    const navigationHandlers = useRef(null);

    const updatePlayerState = (update) => {
        setPlayerState((prev) => {
            console.log('updatePlayerState', update);
            return { ...prev, ...update };
        });
    };

    const setLessonObject = (lessonObject, pageID /* changedLessons */) => {
        const lessonNode = lessonObject.lessonNode;
        lessonObject.nodeMap[lessonNode.nodeID] = lessonNode; // point nodeMap[] lesson node at lessonObject.lessonNode, to sync with editor
        lessonNode.levelTitle = lessonNode.levelTitle.toLowerCase().replace(/^lv\./, ''); // patch old level titles
        wireNodeTree(lessonNode, lessonObject.nodeMap);
        setCurrentPage(lessonObject, pageID);
    };

    const setCurrentPage = (lessonObject, pageID) => {
        const currentLesson = lessonObject.lessonNode;
        let currentSection = currentLesson.children[0];
        let currentPage = currentSection.children[0];
        let currentStep = currentPage.children[0];
        if (pageID && lessonObject.nodeMap[pageID]) {
            // opening a specific page, find & update currents
            currentPage = lessonObject.nodeMap[pageID];
            currentSection = currentPage.parent;
            currentStep = currentPage.children[0];
        }
        // max of 4 visible main sections
        const mainSections = currentLesson.sections.navMenu.slice(0, 4).map(s => ({ ...s, firstPageNodeID: currentLesson.sections.firstPageNodeIDs[s.title] }));
        //
        updatePlayerState({
            mainSections,
            sectionIndex: currentSection.index, pageIndex: currentPage.index, step: 0,
            currentLesson, currentSection, currentStep, currentPage,
            featuresLesson: currentLesson.title === 'Lessons features',
            glossaryPhrases: new Set(),
        });
    };

    const reloadPage = () => {
        global.update({ playerUpdateTrigger: global.playerUpdateTrigger + 1 });
    };

    const clear = () => setPlayerState({ ...playerStateInit });

    const next = async (forceNext) => {
        // // if first-time visitor, redirect to feature page when they exit a oage visited via a direct lesson or page URL
        // if (cookies.passfirst !== 'true' && !playerState.currentLesson.guestAccess) {
        //     global.update({ showPreviewNoticeDialog: true });
        //     return;
        // }

        // play an interstitial, if enabled & at interstitial page-rate interval
        if (userContext.user.accessKeys && !global.currentLessonObject.lessonNode.guestAccess) {
            const url = `${lessonsPaths.player}/${global.currentLessonObject._id}`;
            const feature = featureFor({ type: 'lesson', objectID: global.currentLessonObject._id, url });
            const accessKey = accessKeys?.[feature];
            if (accessKey?.interstitialRate && accessKey.adUnits.InterstitialAd && !getProOrStaff(userContext.user) && window.flutter_inappwebview) {
                if (progressContext.pagesVisited.length > 1 && progressContext.pagesVisited.length % accessKey.interstitialRate === 0) {
                    window.flutter_inappwebview.callHandler('ShowAd', {
                        type: 'InterstitialAd',
                        unitId: accessKey.adUnits.InterstitialAd
                    });
                }
            }
        }
        // first allow page-specific navigation overrides
        if (navigationHandlers.current && navigationHandlers.current.next && forceNext !== true) {
            return navigationHandlers.current.next();
        }
        navigationHandlers.current = null; // set optionally by each page rendering component

        // advance lesson step through steps, pages, sections
        const fromPage = playerState.currentPage;
        const skipOrNextAction = skipOrNext(fromPage).match(/skip/) ? 'skip' : 'next';
        const fromContext = currentContext();
        // console.log(`** > next page=(${playerState.currentPage.nodeID},${playerState.currentPage.index}),${playerState.currentSection.nodeID},section=(${playerState.currentSection.index},${playerState.currentSection.children && playerState.currentSection.children.length}),step=(${playerState.currentStep.nodeID},${playerState.currentStep.index})`);
        // console.log('next fromContext', fromContext);
        let { step, pageIndex, sectionIndex } = playerState;
        if (step < playerState.currentPage.children.length - 1)
            step += 1;
        else if (pageIndex < playerState.currentSection.children.length - 1) {
            step = 0;
            pageIndex += 1;
        } else if (playerState.currentLesson.children[sectionIndex].miniLesson) {
            // advancing out of mini-lesson does a return to caller
            if (playerState.featuresLesson) {
                navigate(lessonsPaths.features);
                return;
            }
            step = 0;
            try {
                // return from mini-lesson
                const callingPageNodeID = progressContext.miniLessonEnters.find(enter => enter.to.section === playerState.currentSection.nodeID).from.page;
                const returnPage = global.currentLessonObject.nodeMap[callingPageNodeID];
                pageIndex = returnPage.index;
                sectionIndex = returnPage.parent.index;
                const returnContext = { page: returnPage.nodeID, section: returnPage.parent.nodeID };
                await postEvent({ action: 'leaveMiniLesson', context: fromContext, details: { to: returnContext } });
            } catch (e) {
                // any errors returning out of an apparent call into a mini-lesson will exit lesson, but post and end event
                endEditLesson(playerState.currentLesson.lessonObjectID);
                // navigate(lessonsPaths.lessons);
                window.location.href = lessonsPaths.lessons; // make visible to any embeding app
                return;
            }
        } else {
            // skip any mini-lessons not in main stepping sequence
            do {
                sectionIndex += 1;
            } while (sectionIndex < playerState.currentLesson.children.length && playerState.currentLesson.children[sectionIndex].miniLesson);
            if (sectionIndex < playerState.currentLesson.children.length) {
                step = 0;
                pageIndex = 0;
            } else {
                // end of lesson, back to MyLessons home
                const result = await postEvent({ action: 'end' }, playerState.currentLesson);
                GTM.userAction('end', { lesson: playerState.currentLesson.title }, 'lessons');
                // console.log('posted end event', result);
                endEditLesson(playerState.currentLesson.lessonObjectID);
                // navigate(lessonsPaths.lessons);
                window.location.href = lessonsPaths.lessons; // make visible to any embeding app
                return;
            }
        }
        //
        const currentSection = playerState.currentLesson.children[sectionIndex];

        let progress = progressContext;

        // the goal-check section has been deprecated, skip over it, recording effectives steps through
        if (currentSection.type === 'GoalCheck') {
            // emulate playing of the hidden pages
            let toSection = currentSection;
            let pages = currentSection.children;
            let toPage, page;
            for (page of pages) {
                pageIndex = page.index;
                if (page.componentName === 'GoalCheckSuccess') {
                    break;
                }
                const step = page.children[0];
                // check for exercise on the page & mark complete
                for (const e of step.children.filter(e => e.exercise)) {
                     await postEvent({ action: 'complete' }, e);
                }

                if (page.index < pages.length - 1) {
                    toPage = pages[page.index + 1];
                    if (toPage.componentName !== 'XXGoalCheckSuccess') {
                        const toStepID = toPage.children[0].nodeID;
                        const progressData = await postEvent({
                            action: 'next',
                            context: { step: step.nodeID, page: page.nodeID, section: currentSection.nodeID },
                            details: {
                                to: { step: toStepID, page: toPage.nodeID, section: toSection.nodeID }
                            }
                        });
                        progress.stepsLeft = progressData.stepsLeft;  //  hack here to get around delayed context state updates!
                        progress.exercisesLeft = progressData.exercisesLeft;
                    }
                }
            }
            if (page.componentName !== 'GoalCheckSuccess') {
                const progressData = await postEvent({ action: 'end' }, playerState.currentLesson);
                progress.stepsLeft = progressData.stepsLeft;  //  hack here to get around delayed context state updates!
                progress.exercisesLeft = progressData.exercisesLeft;
                GTM.userAction('end', { lesson: playerState.currentLesson.title }, 'lessons');
                // end of lesson, so quit to mylessons page
                window.location.href = lessonsPaths.lessons; // make visible to any embeding app
                return;
            }
        }

        const currentPage = currentSection.children[pageIndex];
        const currentStep = currentPage.children[step];

        // check to ensure we've finished if next page is 'GoalCheckSucces', the last page in all lessons
        if (currentPage.componentName === 'GoalCheckSuccess' && !global.showEditor && (progress.stepsLeft > 0 || progress.exercisesLeft > 0)) {
            global.update({ showExitConfirmDialog: true });
        } else {
            // following is now done via call to setCurrentPage() in Player.js
            //updatePlayerState({ step, pageIndex, sectionIndex, currentStep, currentPage, currentSection });
            //global.update({ currentPageID: currentPage.nodeID });
            //
            const nextContext = { currentStep, currentPage, currentSection };
            // console.log('next nextContext', nextContext);
            // console.log(`** < next page=(${currentPage.nodeID},${currentPage.index}),section=(${currentSection.index},${currentSection.children && currentSection.children.length}),step=(${currentStep.nodeID},${currentStep.index},${currentStep.children && currentStep.children.length})`);
            await postEvent({ action: 'next', context: fromContext, details: { to: currentContext(null, nextContext) } });
            GTM.userAction(skipOrNextAction, { lesson: playerState.currentLesson.title, from: fromPage.componentName, to: currentPage.componentName }, 'lessons');
            // update current lesson page URL
            const pageURL = makePageURL(global.currentLessonObject._id, currentPage.nodeID);
            navigate(pageURL);
        }
    };

    const back = async (forceBack) => {
        // go back through steps, pages, sections

        // // if first-time visitor, redirect to feature page when they exit a oage visited via a direct lesson or page URL
        // if (cookies.passfirst !== 'true' && !playerState.currentLesson.guestAccess) {
        //     global.update({ showPreviewNoticeDialog: true });
        //     return;
        // }
        // first allow page-specific navigation overrides
        if (forceBack !== true && navigationHandlers.current && navigationHandlers.current.back)
            return navigationHandlers.current.back();
        navigationHandlers.current = null; // set optionally by each page rendering component

        const fromContext = currentContext();
        const fromPage = playerState.currentPage;
        let { step, pageIndex, sectionIndex } = playerState;
        let currentSection = playerState.currentLesson.children[sectionIndex];
        let currentPage = currentSection.children[pageIndex];
        let currentStep = currentPage.children[step];
        if (step > 0)
            step -= 1;
        else if (pageIndex > 0) {
            pageIndex -= 1;
            currentPage = currentSection.children[pageIndex];
            step = playerState.currentPage.children.length - 1;
        } else if (currentSection.miniLesson) {
            // not in regular section-sequencing, a mini-lesson, return to calling page
            if (playerState.featuresLesson) {
                navigate(lessonsPaths.features);
                return;
            }
            step = 0;
            try {
                const callingPageNodeID = progressContext.miniLessonEnters.find(enter => enter.to.section === currentSection.nodeID).from.page;
                currentPage = global.currentLessonObject.nodeMap[callingPageNodeID];
                pageIndex = currentPage.index;
                currentSection = currentPage.parent;
                sectionIndex = currentSection.index;
            } catch (e) {
                // any errors returning out of an apparent call into a mini-lesson will exit lesson, but post and end event
                endEditLesson(playerState.currentLesson.lessonObjectID);
                // navigate(lessonsPaths.lessons);
                window.location.href = lessonsPaths.lessons; // make visible to any embeding app
                return;
            }
        } else if (sectionIndex > 0) {
            // skip past mini-lesson sections, they are not in the normal lesson stepping sequence
            do {
                sectionIndex -= 1;
                currentSection = playerState.currentLesson.children[sectionIndex];
            } while (sectionIndex > 0 && currentSection.miniLesson);
            if (currentSection.miniLesson)
                return;
            pageIndex = currentSection.children.length - 1;
            currentPage = currentSection.children[pageIndex];
            step = currentPage.children.length - 1;
        } else {
            return; // Hey, should this do an exit to My Lessons?
        }
        //
        currentStep = currentPage.children[step];
        updatePlayerState({ step, pageIndex, sectionIndex, currentStep, currentPage, currentSection });
        global.update({ currentPageID: currentPage.nodeID });
        //
        const nextContext = { currentStep, currentPage, currentSection };
        await postEvent({ action: 'back', context: fromContext, details: { to: currentContext(null, nextContext) } });
        GTM.userAction('back', { lesson: playerState.currentLesson.title, from: fromPage.componentName, to: currentPage.componentName }, 'lessons');

        // update current lesson page URL
        const pageURL = makePageURL(global.currentLessonObject._id, currentPage.nodeID);
        navigate(pageURL);
    };

    const replay = () => {
        if (navigationHandlers.current && navigationHandlers.current.replay)
            return navigationHandlers.current.replay();
    };

    // next button label 'skip' or 'next'? - 'skip' if incomplete quiz or stepped-process on the current page or page is flagged optional
    const skipOrNext = (page) => {
        if (playerState.featuresLesson)
            return 'next';
        if (navigationHandlers.current && navigationHandlers.current.skipOrNext)
            return navigationHandlers.current.skipOrNext(page);
        const completedThisPage = progressContext.completedExercises.reduce((p, c) => p + (c.page === page.nodeID ? 1 : 0), 0) || 0;
        return page.optional || completedThisPage < page.exerciseCount ? 'skip' : 'next';
    };

    const registerNavigation = (navHandlers) => navigationHandlers.current = navHandlers;

    const redrawNavButtons = () => progressContext.update({});

    const finish = async () => {
        // explicit end of lesson, back to MyLessons home
        await postEvent({ action: 'end' }, playerState.currentLesson);
        endEditLesson(playerState.currentLesson.lessonObjectID);
        navigateExternal(lessonsPaths.overview);  // to force a recompute of the lesson progress (Hey, do this more efficiently!)
        
        // after finish lesson, surver banner will come up
        // const current = new Date();
        // const nextSurvey = cookies['survey-1.0'];
        // if (!nextSurvey || (nextSurvey && new Date(nextSurvey).getTime() <= current.getTime())) {
        //     setCookies('survey-1.0', current, { path: '/', domain: cookieDomain', maxAge: 1 * 365 * 24 * 60 * 60 });
        // }
        // navigate({ pathname: lessonsPaths.lessons, state: 'finished' });
    };

    const endEditLesson = async (lessonID) => {
        console.log('player.endEditLesson for', userContext.user?.name)
        if (userContext.user) {
            try {
                await httpAPI('', apiPaths.editLock, { data: { lessonID, userID: userContext.user && userContext.user._id, action: 'release' } });
            } catch (e) {
                console.log('end-edit failed');
            }
        }
    };

    // are any of the modes in given list currently active?
    const inActiveMode = (modes) => playerState.activeModes.some(am => modes.includes(am));

    // sets current active mode(s)
    const setMode = (...modes) => {
        playerState.activeModes = [...modes];
        updatePlayerState({ activeModes: playerState.activeModes });
    };

    // activates additional mode(s)
    const activateMode = (...modes) => {
        modes.forEach(m => {
            if (!playerState.activeModes.includes(m))
                playerState.activeModes.push(m);
        });
        updatePlayerState({ activeModes: playerState.activeModes });
    };

    // deactivate given mode(s)
    const deactivateMode = (...modes) => {
        modes.forEach(m => {
            if (playerState.activeModes.includes(m))
                playerState.activeModes.splice(playerState.activeModes.indexOf(m), 1);
        });
        updatePlayerState({ activeModes: playerState.activeModes });
    };

    // get named field value from element
    const fieldValue = (element, field) => {
        const f = element.fieldsByName[field];
        if (f.src) {
            // indirect reference, retrieve value from specified source object & prop
            const [obj, prop] = f.src.split('.');
            const srcObj = {
                lesson: playerState.currentLesson,
                section: playerState.currentSection,
                page: playerState.currentPage,
                step: playerState.currentStep }[obj];
            return srcObj[prop];
        } else {
            return f.value;
        }
    };

    const gotoPage = async (destination, options = {}) => {
        // step to page of given title
        let destPage;
        playerState.currentLesson.children.find(cs => cs.children.find(p => {
            if (p.title === destination) {
                destPage = p;
                return true;
            }
        }));

        if (!destPage) {
            if (options.fallbackPageType) {
                // find first page of given type if not found by name
                playerState.currentLesson.children.find(cs => cs.children.find(p => {
                    if (p.componentName === options.fallbackPageType) {
                        destPage = p;
                        return true;
                    }
                }));
            } else if (options.page) {
                destPage = options.page;
            }
        }
        if (destPage) {
            const fromContext = currentContext();
            // advance current step/page/section state
            const currentPage = destPage;
            const currentSection = currentPage.parent;
            const step = 0;
            const currentStep = currentPage.children[step];
            const [pageIndex, sectionIndex] = [currentPage.index, currentSection.index];
            updatePlayerState({ step, pageIndex, sectionIndex, currentStep, currentPage, currentSection });
            //
            const nextContext = { currentStep, currentPage, currentSection };
            // console.log('gotoPage nextContext', nextContext);
            await postEvent({ action: 'gotoPage', context: fromContext, details: { to: currentContext(null, nextContext) } });
            // update current lesson page URL
            const pageURL = makePageURL(global.currentLessonObject._id, currentPage.nodeID);
            navigate(pageURL);
        }
    };

    const showPage = (lessonObjectID, pageNodeID) => {
        if (global.currentLessonObject._id === lessonObjectID) {
            if (playerState.currentPage.nodeID === pageNodeID)
                return;
        }
        const pageURL = makePageURL(lessonObjectID, pageNodeID);
        navigate(pageURL, { replace: true });
    };

    const elementByName = (name) => playerState.currentStep.children.find(e => e.name === name);

    const elementsByType = (type) => playerState.currentStep.children.filter(e => e.componentName === type);

    // return current lesson play context as a node + ancestor node-id chain.  If element undefined, start at current step
    const currentContext = (element, currentState) => {
        const elementCtx = element ? { element: element.nodeID } : { };
        const state = currentState || playerState;
        return {
            step: state.currentStep.nodeID,
            page: state.currentPage.nodeID,
            section: state.currentSection.nodeID,
            ...elementCtx,
        };
    };

    // find section by name
    const findSection = (sectionName) => playerState.currentLesson.children.find(cs => cs.title === sectionName);

    const exerciseComplete = (page, element) => {
        return completedExercises.current.find(ce => ce.page === page.nodeID && ce.element === element.nodeID);
    };

    // post a user action event, element optional
    const postEvent = async (event, element) => {
        if (playerState.featuresLesson)
            return;
        const eventToPost = element ? { context: currentContext(element), ...event } : event;
        try {
            await postLessonEvent(global.currentLessonObject, eventToPost);
        } catch (e) {
            console.log(`Event post error - ${e}`);
        }
        if (progressContext && global.currentLessonObject) {
            const progress = await httpAPI('', `${apiPaths.getLessonProgress}/${global.currentLessonObject._id}`, { method: 'GET' });
            // console.log('updating progress', progress.data);
            progressContext.update({ ...progress.data });
            return progress.data;
        }
    };

    const makePageURL = (lessonID, pageID, queryParams) => {
        const lid = lessonID === '.' ? global.currentLessonObject._id : lessonID;
        const pid = pageID === '.' ? playerState.currentPage.nodeID : pageID;
        const qparams = [...(queryParams || [])];
        if (global.showEditor) qparams.push('edit=1');
        const qp = qparams.length > 0 ? `?${qparams.join('&')}` : '';
        return `${lessonsPaths.player}/${lid}${pid ? `/${pid}` : ''}${qp}`;
    };

    // posts.patterns.<lesson_title>.lesson, in title replaces whitespace with '_' and periods with '$' to honor h-tag syntax
    const contextTagForCurrentLesson = () => {
        const titleForDiscussions = global.currentLessonObject.lessonNode.libraryPattern || global.currentLessonObject.title;
        return `posts.patterns.${titleForDiscussions.replace(/ /g, '_').replace(/\./g, '$')}.lesson`;
    };

    // PlayerContext value & public API
    return {
        ...playerState,
        update: updatePlayerState,
        setLessonObject,
        setNavigate: (n) => navigate = n,
        setCurrentPage,
        reloadPage,
        showPage,
        contextTagForCurrentLesson,
        fieldValue,
        inActiveMode, setMode, activateMode, deactivateMode,
        next, back, gotoPage, finish, skipOrNext, replay,
        registerNavigation, redrawNavButtons,
        postEvent, currentContext,
        clear,
        findSection, elementByName, elementsByType,
        setProgressContext: (ctx) => progressContext = ctx,
        setCompletedExercises: (ce) => {
            completedExercises.current = [...ce];
        }, // cache a copy as original object may be clobberedd by progress context code
        exerciseComplete,
        makePageURL,
        endEditLesson,
    };
};

export default PlayerContext;
